import React, { useState } from 'react';
import { ChevronRight, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import BaseModal from '../../components/common/BaseModal/BaseModal';
import './FailureReasonModal.css';

const errorTypeConfig = {
    connection_error: {
        headerLabel: 'Comparison Connection Failed',
        subtitleEntity: 'Connection',
        showSchemaPair: false,
    },
    schema_error: {
        headerLabel: 'Schema Pairs Failed',
        subtitleEntity: 'Schema',
        showSchemaPair: true,
    },
};

// Fallback so the modal never crashes on an unrecognised error_type
const defaultConfig = {
    headerLabel: 'Failed',
    subtitleEntity: 'Item',
    showSchemaPair: false,
};

// Shared with JobRow's Attempt History "Execution Summary" column so the link
// text always matches this modal's own header label.
export const getFailureSummaryLabel = (errorData) => {
    const errorTypeKey = errorData?.[0]?.error_type;
    const config = errorTypeConfig[errorTypeKey] ?? defaultConfig;
    const showCount = errorTypeKey === 'schema_error';
    const totalFailedPairs = errorData?.length ?? 0;
    return showCount ? `${config.headerLabel} (${totalFailedPairs})` : config.headerLabel;
};

const FailureReasonModal = ({ isOpen, onClose, errorData }) => {
    const [expandedIndexes, setExpandedIndexes] = useState({});

    const errorTypeKey = errorData?.[0]?.error_type;
    const config = errorTypeConfig[errorTypeKey] ?? defaultConfig;

    const totalFailedPairs = errorData?.length ?? 0;
    const showCount = errorTypeKey === 'schema_error';

    const toggleReadMore = (index) => {
        setExpandedIndexes(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleCopyOne = async (errorStr) => {
        try {
            await navigator.clipboard.writeText(errorStr);
            toast.success('Error message copied successfully');
        } catch {
            toast.error('Failed to copy to clipboard');
        }
    };

    const handleCopyAll = async () => {
        const allErrors = errorData.map(item => item.error).join('\n\n');
        try {
            await navigator.clipboard.writeText(allErrors);
            toast.success('All errors copied successfully');
        } catch {
            toast.error('Failed to copy to clipboard');
        }
    };

    // Split on first \n → short (human-readable) + technical (Snowflake detail)
    const parseError = (errorStr) => {
        const newlineIndex = errorStr.indexOf('\n');
        if (newlineIndex === -1) return { short: errorStr, technical: '' };
        return {
            short: errorStr.substring(0, newlineIndex),
            technical: ' ' + errorStr.substring(newlineIndex + 1),
        };
    };

    // Split on first '. ' → bold first sentence + plain remainder
    const splitFirstSentence = (str) => {
        const dotIndex = str.indexOf('. ');
        if (dotIndex === -1) return [str, ''];
        return [str.substring(0, dotIndex + 1), str.substring(dotIndex + 1)];
    };

    const handleOnClose = () => {
        setExpandedIndexes({});
        onClose?.();
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleOnClose}
            title="Failure Reason"
            className="run-failed-modal"
            footer={
                <>
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={handleOnClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCopyAll}
                    >
                        Copy
                    </button>
                </>
            }
        >
            {errorData && errorData.length > 0 ? (
                <div className="frm-body">
                    <p className="frm-pairs-header">
                        {showCount
                            ? `${config.headerLabel} (${totalFailedPairs}):`
                            : `${config.headerLabel}:`
                        }
                    </p>

                    {/* Error Blocks */}
                    <div className="frm-pairs-list">
                        {errorData.map((errorItem, index) => {
                            const { short, technical } = parseError(errorItem.error);
                            const [firstSentence, remainder] = splitFirstSentence(short);
                            const isExpanded = !!expandedIndexes[index];

                            // For schema_error: use source/target schema based on side
                            // For connection_error: use connection_name
                            const entityName = errorTypeKey === 'schema_error'
                                ? (errorItem.side === 'source' ? errorItem.source_schema : errorItem.target_schema)
                                : errorItem.connection_name;

                            const sideLabel = errorItem.side === 'target' ? 'Target' : 'Source';

                            return (
                                <div key={index} className="frm-pair-block">
                                    {config.showSchemaPair ? (
                                        /* schema_error: schema pair row on top, subtitle below */
                                        <>
                                            <div className="frm-pair-header">
                                                <div className="frm-schema-pair">
                                                    <span className="frm-schema-name">{errorItem.source_schema}</span>
                                                    <span className="frm-schema-arrow-wrapper">
                                                        <ChevronRight style={{ width: '0.729vw', height: '0.729vw', color: '#94a3b8' }} />
                                                    </span>
                                                    <span className="frm-schema-name">{errorItem.target_schema}</span>
                                                </div>
                                                <button
                                                    className="frm-copy-btn"
                                                    onClick={() => handleCopyOne(errorItem.error)}
                                                    title="Copy error"
                                                >
                                                    <Copy style={{ width: '0.833vw', height: '0.833vw' }} />
                                                </button>
                                            </div>
                                            <p className="frm-subtitle">
                                                {sideLabel} {config.subtitleEntity} '<strong>{entityName}</strong>' failed.
                                            </p>
                                        </>
                                    ) : (
                                        /* connection_error: subtitle and copy button on the same line */
                                        <div className="frm-pair-header">
                                            <p className="frm-subtitle frm-subtitle--inline">
                                                {sideLabel} {config.subtitleEntity} '<strong>{entityName}</strong>' failed.
                                            </p>
                                            <button
                                                className="frm-copy-btn"
                                                onClick={() => handleCopyOne(errorItem.error)}
                                                title="Copy error"
                                            >
                                                <Copy style={{ width: '0.833vw', height: '0.833vw' }} />
                                            </button>
                                        </div>
                                    )}
                                    {/* Short error: first sentence bold, rest normal, technical detail inline on expand */}
                                    <p className="frm-short-error">
                                        <strong>{firstSentence}</strong>
                                        {remainder}
                                        {technical && (
                                            <>
                                                {isExpanded && (
                                                    <span className="frm-technical-inline">{technical}</span>
                                                )}
                                                {' '}
                                                <span
                                                    className="frm-read-more"
                                                    onClick={() => toggleReadMore(index)}
                                                >
                                                    {isExpanded ? 'Read less...' : 'Read more...'}
                                                </span>
                                            </>
                                        )}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <p className="failure-reason-text failure-reason-error">
                    No error details available.
                </p>
            )}
        </BaseModal>
    );
};

export default FailureReasonModal;